import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Input,
  Text,
  Badge,
  HStack,
  Select,
  Flex,
  Spacer,
  Button,
} from '@chakra-ui/react';
import FloatingAddButton from '../../components/FloatingAddButton';
import TableSkeleton from '../../components/TableSkeleton';
import { useEffect, useMemo, useState } from 'react';
import { MdEdit, MdVisibility, MdHistory, MdMedicalServices, MdInventory, MdPictureAsPdf } from 'react-icons/md';
import { useToast } from '@chakra-ui/react';
import juzgadoApi from '../../services/juzgadoApi';
import type { Detencion } from './juzgadoTypes';
import ModalDetencion from './ModalDetencion';
import ModalHistoricoJuzgado from './ModalHistoricoJuzgado';
import ModalControlMedico from './ModalControlMedico';
import ModalPertenencias from './ModalPertenencias';

export default function TablaDetenciones() {
  const [detenciones, setDetenciones] = useState<Detencion[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const toast = useToast();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'new' | 'edit' | 'view'>('new');
  const [detencionSel, setDetencionSel] = useState<Detencion | null>(null);

  const [modalHistOpen, setModalHistOpen] = useState(false);
  const [modalMedicoOpen, setModalMedicoOpen] = useState(false);
  const [modalPertOpen, setModalPertOpen] = useState(false);

  const norm = (v?: string | null) => (v || '').toLowerCase();

  const fmtFecha = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-MX');
  };

  const colorEstatus = (e?: string) => {
    if (!e) return 'gray';
    const s = e.toLowerCase();
    if (s.includes('activa')) return 'green';
    if (s.includes('resuelta')) return 'blue';
    if (s.includes('cancelada')) return 'gray';
    return 'gray';
  };

  const colorEstatusDetenido = (e?: string) => {
    const s = (e || '').toLowerCase();
    if (s.includes('liberado')) return 'green';
    if (s.includes('canalizado')) return 'blue';
    return 'orange';
  };

  const fetchDetenciones = async () => {
    try {
      setLoading(true);
      const data = await juzgadoApi.listarDetenciones(filtroEstatus ? { estatus: filtroEstatus } : undefined);
      setDetenciones(data);
    } catch (e: any) {
      toast({ title: 'Error cargando detenciones', description: e.message, status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDetenciones(); }, [filtroEstatus]);

  const filtradas = useMemo(() => {
    const f = norm(filtro);
    if (!f) return detenciones;
    return detenciones.filter(d =>
      norm(d.folio).includes(f) ||
      norm(d.nombres).includes(f) ||
      norm(d.apellidoPaterno).includes(f) ||
      norm(d.curp).includes(f) ||
      norm(d.tipoDetencion).includes(f)
    );
  }, [detenciones, filtro]);

  const abrirNuevo = () => {
    setDetencionSel(null);
    setModalMode('new');
    setModalOpen(true);
  };

  const abrirEditar = (d: Detencion) => {
    setDetencionSel(d);
    setModalMode('edit');
    setModalOpen(true);
  };

  const abrirVer = (d: Detencion) => {
    setDetencionSel(d);
    setModalMode('view');
    setModalOpen(true);
  };

  const handleGuardado = (d: Detencion) => {
    setDetenciones(prev => {
      const idx = prev.findIndex(x => x.id === d.id);
      if (idx >= 0) { const c = [...prev]; c[idx] = d; return c; }
      return [d, ...prev];
    });
    setModalOpen(false);
  };

  return (
    <Box p={4} pt="79px" position="relative">
      <Text fontSize="2xl" fontWeight="bold" mb={4} color="#5b182b">
        Detenciones
      </Text>

      <Flex mb={4} gap={2} wrap="wrap">
        <Input
          placeholder="Buscar por folio, nombre, CURP..."
          value={filtro}
          onChange={e => setFiltro(e.target.value)}
          maxW="340px"
          borderColor="#bb8c52"
          _focus={{ borderColor: '#5b182b', boxShadow: '0 0 0 1px #5b182b' }}
        />
        <Select
          placeholder="Todos los estatus"
          value={filtroEstatus}
          onChange={e => setFiltroEstatus(e.target.value)}
          maxW="200px"
          borderColor="#bb8c52"
        >
          <option value="Activa">Activa</option>
          <option value="Resuelta">Resuelta</option>
          <option value="Cancelada">Cancelada</option>
        </Select>
        <Spacer />
        <Button
          as="a"
          href={juzgadoApi.pdfReporteDetenciones(filtroEstatus ? { estatus: filtroEstatus } : undefined)}
          target="_blank"
          leftIcon={<MdPictureAsPdf />}
          colorScheme="red"
          variant="outline"
        >
          Reporte PDF
        </Button>
      </Flex>

      {loading ? (
        <Box bg="white" borderRadius="lg" shadow="sm" overflow="hidden">
          <TableSkeleton rows={8} columns={7} />
        </Box>
      ) : (
        <Box bg="white" borderRadius="lg" shadow="sm" overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg="#5b182b">
              <Tr>
                <Th color="white">Folio</Th>
                <Th color="white">Detenido</Th>
                <Th color="white">Tipo</Th>
                <Th color="white">Fecha</Th>
                <Th color="white">Estatus detenido</Th>
                <Th color="white">Estatus</Th>
                <Th color="white">Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtradas.length === 0 && (
                <Tr>
                  <Td colSpan={7} textAlign="center" color="gray.400" py={8}>
                    No hay detenciones registradas
                  </Td>
                </Tr>
              )}
              {filtradas.map(d => (
                <Tr key={d.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="medium" color="#5b182b" whiteSpace="nowrap">{d.folio}</Td>
                  <Td>
                    <Text fontWeight="medium">{d.apellidoPaterno} {d.apellidoMaterno}, {d.nombres}</Text>
                    {d.alias && <Text fontSize="xs" color="gray.500">"{d.alias}"</Text>}
                  </Td>
                  <Td fontSize="sm">{d.tipoDetencion}</Td>
                  <Td whiteSpace="nowrap" fontSize="sm">{fmtFecha(d.fechaHecho ?? d.createdAt)}</Td>
                  <Td>
                    <Badge colorScheme={colorEstatusDetenido(d.estatusDetenido)} px={2} borderRadius="full">
                      {d.estatusDetenido}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={colorEstatus(d.estatus)} px={2} borderRadius="full">
                      {d.estatus}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <IconButton
                        aria-label="Ver detención"
                        icon={<MdVisibility />}
                        size="sm"
                        bg="#5b182b"
                        color="white"
                        _hover={{ bg: '#691f30' }}
                        onClick={() => abrirVer(d)}
                      />
                      <IconButton
                        aria-label="Editar detención"
                        icon={<MdEdit />}
                        size="sm"
                        bg="#bb8c52"
                        color="white"
                        _hover={{ bg: '#a67c47' }}
                        onClick={() => abrirEditar(d)}
                      />
                      <IconButton
                        aria-label="Control médico"
                        icon={<MdMedicalServices />}
                        size="sm"
                        colorScheme="teal"
                        variant="solid"
                        onClick={() => { setDetencionSel(d); setModalMedicoOpen(true); }}
                      />
                      <IconButton
                        aria-label="Pertenencias"
                        icon={<MdInventory />}
                        size="sm"
                        colorScheme="purple"
                        variant="solid"
                        onClick={() => { setDetencionSel(d); setModalPertOpen(true); }}
                      />
                      <IconButton
                        aria-label="PDF Acta"
                        icon={<MdPictureAsPdf />}
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        as="a"
                        href={juzgadoApi.pdfActaDetencion(d.id)}
                        target="_blank"
                        title="Descargar acta de detención"
                      />
                      <IconButton
                        aria-label="Historial"
                        icon={<MdHistory />}
                        size="sm"
                        colorScheme="gray"
                        variant="outline"
                        onClick={() => { setDetencionSel(d); setModalHistOpen(true); }}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <ModalDetencion
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        detencion={detencionSel}
        onGuardado={handleGuardado}
      />

      <ModalHistoricoJuzgado
        isOpen={modalHistOpen}
        onClose={() => setModalHistOpen(false)}
        entidad="Detencion"
        entidadId={detencionSel?.id ?? 0}
        titulo={`Historial: ${detencionSel?.folio ?? ''}`}
      />

      <ModalControlMedico
        isOpen={modalMedicoOpen}
        onClose={() => setModalMedicoOpen(false)}
        detencion={detencionSel}
        onGuardado={fetchDetenciones}
      />

      <ModalPertenencias
        isOpen={modalPertOpen}
        onClose={() => setModalPertOpen(false)}
        detencion={detencionSel}
      />

      <FloatingAddButton text="Nueva Detención" onClick={abrirNuevo} />
    </Box>
  );
}
